import {FormLabel} from '@mui/material';
import {useMemo, useRef, useState} from 'react';
import moment, {Moment} from 'moment';
import {Controller, UseFormReturn} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../../i18n/translations';
import {FlatMultiTextSelect, Option} from '../../../flat-multi-text-select/flat-multi-text-select';
import {CompactModal} from '../../../modal/compact-modal';
import FlatInput from '../../../flat-input/flat-input';
import {FlatSelectDate} from '../../../flat-select-date/flat-select-date';
import {FlatStartsAtTime} from '../../../flat-starts-at-time/flat-starts-at-time';
import {ModalProps} from '../../../modal/modal';
import {FlatSelectDropdown} from '../../../flat-select-dropdown/flat-select-dropdown';
import {ActivityTypeScheduleForm} from '../../../activity-type-schedule-form/activity-type-schedule-form';
import {ActivityTypeSchedule, SchedulerTypes} from '../../../../types';
import {FetchEntityUsersResult} from '../../../../hooks/use-fetch-entity-users';
import {CustomCheckbox} from '../../../checkbox/checkbox';
import {CareServiceForm} from '../data/care-service-item';
import {
  transformActivityTypeScheduleToCareServiceRepetition,
  transformCareServiceRepetitionToActivityTypeSchedule,
} from '../data/care-service-data-transformation';
import {CareServicesInputColor} from './care-services-input-color';

export type CareServicesBaseModalProps = {
  form: UseFormReturn<CareServiceForm>;
  caregivers: FetchEntityUsersResult;
  seniors: FetchEntityUsersResult;
  disabled?: boolean;
  onDelete?: () => void;
} & ModalProps;

export function CareServicesBaseModal({
  form,
  caregivers,
  seniors,
  disabled,
  onDelete,
  ...rest
}: CareServicesBaseModalProps) {
  const prevHours = useRef<{startDate: Moment; endDate: Moment}>({
    startDate: form.getValues('startDate') ?? moment().minute(0),
    endDate: form.getValues('endDate') ?? moment().minute(0).add(1, 'hour'),
  });
  const [twentyFourToggle, setTwentyFourToggle] = useState(form.getValues('twentyFourHours'));
  const {t} = useTranslation();

  const caregiverOptions = useMemo(
    () =>
      caregivers.items?.map<Option>((c) => ({
        key: c.user.id!,
        label: c.user.displayName ?? c.user.name,
        picture: c.user.picture,
      })) ?? [],
    [caregivers.items]
  );

  const seniorsOptions = useMemo(
    () =>
      seniors.items?.map<Option>((s) => ({
        key: s.user.id!,
        label: s.user.displayName ?? s.user.name,
        picture: s.user.picture,
      })) ?? [],
    [seniors.items]
  );

  return (
    <CompactModal
      {...rest}
      actionButton={
        !disabled && onDelete
          ? {
              type: 'destroy',
              preventAutoClose: true,
              callback: onDelete,
            }
          : undefined
      }
    >
      <div className="care-service-form">
        <div className="row">
          {/* --- Senior Users Input --- */}
          <Controller
            name="targetUserId"
            control={form.control}
            rules={{
              required: {
                value: true,
                message: t(translations.care_services.form.required_field_message),
              },
            }}
            render={({field, fieldState}) => {
              const selectedSenior = seniorsOptions.find((opt) => opt.key === field.value);
              return (
                <FlatSelectDropdown
                  required
                  role="user"
                  label={t(translations.care_services.form.senior_label)}
                  placeholder={t(translations.care_services.form.senior_placeholder)}
                  loading={seniors.isLoading}
                  clearable={false}
                  options={seniorsOptions}
                  value={selectedSenior ?? {key: field.value, label: field.value}}
                  errors={fieldState.error?.message}
                  disabled={disabled}
                  disabledTooltip={t(translations.care_services.form.permissions_disabled_tooltip)}
                  onChange={(selected) => {
                    form.setValue('targetUserId', selected.key, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }}
                />
              );
            }}
          />
        </div>

        <div className="row">
          {/* --- Title Input --- */}
          <Controller
            name="title"
            control={form.control}
            render={({field, fieldState}) => {
              return (
                <FlatInput
                  label={t(translations.care_services.form.title_label)}
                  type="text"
                  autoFocus
                  value={field.value ?? ''}
                  placeholder={t(translations.care_services.form.title_placeholder)}
                  errors={fieldState.error?.message}
                  disabled={disabled}
                  disabledTooltip={t(translations.care_services.form.permissions_disabled_tooltip)}
                  onChange={(evt) => {
                    form.setValue('title', evt.target.value || null, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }}
                />
              );
            }}
          />

          {/* --- Color Picker Input --- */}
          <Controller
            name="color"
            control={form.control}
            rules={{required: true}}
            render={({field, fieldState}) => {
              return (
                <CareServicesInputColor
                  required
                  label={t(translations.care_services.form.color_label)}
                  value={field.value}
                  errors={fieldState.error?.message}
                  disabled={disabled}
                  disabledTooltip={t(translations.care_services.form.permissions_disabled_tooltip)}
                  onChange={(selected: any) => {
                    form.setValue('color', selected.key, {shouldDirty: true, shouldValidate: true});
                  }}
                />
              );
            }}
          />
        </div>

        <div className="row">
          {/* --- 24h Checkbox --- */}
          <Controller
            name="twentyFourHours"
            control={form.control}
            render={({field}) => {
              return (
                <div className="twenty-four-checkbox">
                  <CustomCheckbox
                    id="twenty-four-hours"
                    disabled={disabled}
                    disabledTooltip={t(
                      translations.care_services.form.permissions_disabled_tooltip
                    )}
                    checked={field.value}
                    onChange={(evt) => {
                      const newVal = evt.target.checked;
                      const start = form.getValues('startDate');
                      const end = form.getValues('endDate');

                      // --- Toggle all day mode ---
                      if (newVal) {
                        // Save previous hours
                        prevHours.current = {
                          startDate: start,
                          endDate: end,
                        };

                        // Set 24 hours period
                        form.setValue('startDate', start.clone(), {
                          shouldDirty: true,
                        });
                        form.setValue('endDate', start.clone().add(1, 'day'), {
                          shouldDirty: true,
                        });
                      }
                      // --- Toggle back ---
                      else {
                        // Restore previous hours
                        form.setValue('startDate', prevHours.current.startDate, {
                          shouldDirty: true,
                        });
                        form.setValue('endDate', prevHours.current.endDate, {
                          shouldDirty: true,
                        });
                      }

                      // Update twentyFourHours value
                      form.setValue('twentyFourHours', newVal, {shouldDirty: true});
                      setTwentyFourToggle(newVal);
                      // Trigger validation
                      form.trigger(['startDate', 'endDate']);
                    }}
                  />
                  <FormLabel
                    htmlFor="twenty-four-hours"
                    data-checked={twentyFourToggle}
                    style={{
                      color: disabled ? 'var(--dcm-gray-disable)' : undefined,
                      cursor: disabled ? 'default' : 'pointer',
                    }}
                  >
                    {t(translations.care_services.form.twenty_four_hours_label)}
                  </FormLabel>
                </div>
              );
            }}
          />

          {/* blank space */}
          <div />
        </div>

        <div className="row">
          {/* --- Start Date Input --- */}
          <Controller
            name="startDate"
            control={form.control}
            rules={{required: true}}
            render={({field, fieldState}) => {
              return (
                <FlatSelectDate
                  required
                  label={t(translations.care_services.form.start_date_label)}
                  value={field.value.toDate()}
                  errors={fieldState.error?.message}
                  disabled={disabled}
                  disabledTooltip={t(translations.care_services.form.permissions_disabled_tooltip)}
                  onAccept={(newDate) => {
                    const newStartDate = moment(newDate);
                    const currentEndDate = form.getValues('endDate');
                    form.setValue('startDate', newStartDate, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });

                    // If 24 hours, adjust end date to be next day
                    if (twentyFourToggle) {
                      form.setValue('endDate', newStartDate.clone().add(1, 'day'), {
                        shouldDirty: true,
                      });
                    }
                    // Else, if start date is after end date, adjust end date to match start date time
                    else if (newStartDate.isAfter(currentEndDate, 'day')) {
                      const newEndDate = newStartDate.clone().add(1, 'hour');
                      form.setValue('endDate', newEndDate, {
                        shouldDirty: true,
                      });
                    }
                    // Daily Care Log Limitation:
                    // Else, if start & end date is +24h apart, restrict to 24h
                    else if (currentEndDate.diff(newStartDate, 'hours') >= 24) {
                      const newEndDate = newStartDate.clone().add(1, 'hour');
                      form.setValue('endDate', newEndDate, {
                        shouldDirty: true,
                      });
                    }
                  }}
                />
              );
            }}
          />

          {/* --- End Date Input --- */}
          <Controller
            name="endDate"
            control={form.control}
            rules={{required: true}}
            render={({field, fieldState}) => {
              return (
                <FlatSelectDate
                  required
                  label={t(translations.care_services.form.end_date_label)}
                  value={field.value.toDate()}
                  errors={fieldState.error?.message}
                  disabled={disabled}
                  disabledTooltip={t(translations.care_services.form.permissions_disabled_tooltip)}
                  // @ts-ignore - Daily Care Log Limitation: End date can't be more than 24h after start date
                  maxDate={form.getValues('startDate').clone().add(1, 'day')}
                  onAccept={(newDate) => {
                    const newEndDate = moment(newDate);
                    const currentStartDate = form.getValues('startDate');
                    form.setValue('endDate', newEndDate, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });

                    // If 24 hours, adjust end date to be next day
                    if (twentyFourToggle) {
                      form.setValue('startDate', newEndDate.clone().subtract(1, 'day'), {
                        shouldDirty: true,
                      });
                    }
                    // Else, if end date is before start date, adjust start date to match end date time
                    else if (newEndDate.isBefore(currentStartDate, 'day')) {
                      const newStartDate = newEndDate.clone().add(-1, 'hour');
                      form.setValue('startDate', newStartDate, {
                        shouldDirty: true,
                      });
                    }
                    // Daily Care Log Limitation:
                    // Else, if start & end date is +24h apart, restrict to 24h
                    else if (newEndDate.diff(currentStartDate, 'hours') >= 24) {
                      form.setValue('endDate', currentStartDate.clone().add(1, 'day'), {
                        shouldDirty: true,
                      });
                    }
                  }}
                />
              );
            }}
          />
        </div>

        <div className="row">
          {/* --- Start Hour Input --- */}
          <Controller
            name="startDate"
            control={form.control}
            rules={{required: true}}
            render={({field, fieldState}) => {
              return (
                <FlatStartsAtTime
                  required
                  label={t(translations.care_services.form.start_time_label)}
                  value={field.value.toDate()}
                  errors={fieldState.error?.message}
                  disabled={disabled}
                  disableTooltip={t(translations.care_services.form.permissions_disabled_tooltip)}
                  onAccept={(newDate) => {
                    if (!newDate) return;
                    const newStartDate = moment(newDate);
                    const currentEndDate = form.getValues('endDate');
                    form.setValue('startDate', newStartDate, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });

                    if (twentyFourToggle) {
                      // Adjust end date to be next day
                      form.setValue('endDate', newStartDate.clone().add(1, 'day'), {
                        shouldDirty: true,
                      });
                    }
                    // Else, if start hour is after end hour, adjust end hour.
                    else if (newStartDate.isAfter(currentEndDate, 'hour')) {
                      const newEndDate = newStartDate.clone();
                      newEndDate.add(1, 'hour');

                      form.setValue('endDate', newEndDate, {
                        shouldDirty: true,
                      });
                    }
                    // Daily Care Log Limitation:
                    // Else, if start & end date is +24h apart, restrict to 24h
                    else if (currentEndDate.diff(newStartDate, 'hours') >= 24) {
                      form.setValue(
                        'endDate',
                        currentEndDate.clone().set({
                          hour: newStartDate.hour(),
                          minute: newStartDate.minute(),
                          second: newStartDate.second(),
                        }),
                        {
                          shouldDirty: true,
                        }
                      );
                    }
                  }}
                />
              );
            }}
          />

          {/* --- End Hour Input --- */}
          <Controller
            name="endDate"
            control={form.control}
            rules={{required: true}}
            render={({field, fieldState}) => {
              return (
                <FlatStartsAtTime
                  required
                  label={t(translations.care_services.form.end_time_label)}
                  value={field.value.toDate()}
                  errors={fieldState.error?.message}
                  disabled={twentyFourToggle || disabled}
                  disableTooltip={t(translations.care_services.form.permissions_disabled_tooltip)}
                  shouldDisableTime={(timeValue) => {
                    const currentDate = moment(timeValue);
                    const startDate = form.getValues('startDate');
                    if (!startDate) return false;

                    // 24h max starting from start date
                    const maxEndDate = startDate.clone().add(1, 'day');

                    return currentDate.isAfter(maxEndDate, 'hour');
                  }}
                  onAccept={(newDate) => {
                    if (!newDate) return;
                    const currentStartDate = form.getValues('startDate');
                    const newEndDate = moment(newDate);
                    form.setValue('endDate', newEndDate, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });

                    if (twentyFourToggle) {
                      // Adjust start date to be previous day
                      form.setValue('startDate', newEndDate.clone().subtract(1, 'day'), {
                        shouldDirty: true,
                      });
                    }
                    // Else, if end hour is before start hour, adjust start hour.
                    else if (newEndDate.isBefore(currentStartDate, 'hour')) {
                      const newStartDate = newEndDate.clone().add(-1, 'hour');
                      form.setValue('startDate', newStartDate, {
                        shouldDirty: true,
                      });
                    }
                    // Daily Care Log Limitation:
                    // Else, if start & end date is +24h apart, restrict to 24h
                    else if (newEndDate.diff(currentStartDate, 'hours') >= 24) {
                      form.setValue(
                        'startDate',
                        currentStartDate.clone().set({
                          hour: newEndDate.hour(),
                          minute: newEndDate.minute(),
                          second: newEndDate.second(),
                        }),
                        {
                          shouldDirty: true,
                        }
                      );
                    }
                  }}
                />
              );
            }}
          />
        </div>

        <div className="row">
          {/* --- Repetition Input --- */}
          <Controller
            name="repetition"
            control={form.control}
            render={({field, fieldState}) => {
              return (
                <ActivityTypeScheduleForm
                  type={SchedulerTypes.CareService}
                  open
                  id="repetition"
                  label={t(translations.care_services.form.repetition_label)}
                  parentFormInstance={form}
                  value={transformCareServiceRepetitionToActivityTypeSchedule(field.value)}
                  startAtDateId="date"
                  startAtTimeId="startHour"
                  errors={fieldState.error?.message}
                  disabled={disabled}
                  disabledTooltip={t(translations.care_services.form.permissions_disabled_tooltip)}
                  onClose={() => {
                    const scheduleValue = form.getValues('repetition');
                    if (!scheduleValue || typeof scheduleValue !== 'string') return;

                    // ------ HACK ------
                    // ActivityTypeScheduleForm ACTUALLY outputs a JSON stringified ActivityTypeSchedule type.
                    // We transform it to CareServiceRepetion to conform with api.
                    const activityTypeSchedule: ActivityTypeSchedule = JSON.parse(
                      scheduleValue as any
                    );

                    form.setValue(
                      'repetition',
                      transformActivityTypeScheduleToCareServiceRepetition(activityTypeSchedule),
                      {shouldDirty: true, shouldValidate: true}
                    );
                    // ------ HACK ------
                  }}
                />
              );
            }}
          />
        </div>

        <div className="row">
          {/* --- Responsible Users Input --- */}
          <Controller
            name="responsibleUserIds"
            control={form.control}
            render={({field, fieldState}) => {
              const selectedCaregivers = caregiverOptions.filter((opt) =>
                field.value?.includes(opt.key)
              );
              return (
                <FlatMultiTextSelect
                  label={t(translations.care_services.form.responsible_users_label)}
                  placeholder={t(translations.care_services.form.responsible_users_placeholder)}
                  loading={caregivers.isLoading}
                  hasPictureTag
                  options={caregiverOptions}
                  value={selectedCaregivers}
                  errors={fieldState.error?.message}
                  disabled={disabled}
                  disabledTooltip={t(translations.care_services.form.permissions_disabled_tooltip)}
                  onChange={(selected) => {
                    form.setValue(
                      'responsibleUserIds',
                      typeof selected === 'object'
                        ? (selected as Option[]).map((s) => s.key)
                        : [(selected as Option).key],
                      {shouldDirty: true}
                    );
                  }}
                />
              );
            }}
          />
        </div>

        <div className="row">
          {/* --- Notes Input --- */}
          <Controller
            name="notes"
            control={form.control}
            render={({field, fieldState}) => {
              return (
                <FlatInput
                  type="text"
                  label={t(translations.care_services.form.notes_label)}
                  placeholder={t(translations.care_services.form.notes_placeholder)}
                  multiline
                  minRows={2}
                  value={field.value ?? ''}
                  errors={fieldState.error?.message}
                  disabled={disabled}
                  disabledTooltip={t(translations.care_services.form.permissions_disabled_tooltip)}
                  onChange={(evt) => {
                    form.setValue('notes', evt.target.value || null, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }}
                />
              );
            }}
          />
        </div>
      </div>
    </CompactModal>
  );
}
